import { useEffect, useRef } from "react";

/**
 * Tracks how long an element is in the viewport (in seconds).
 * When the element is fully out of the viewport, it calls onExitView(totalTimeThisSession).
 * Optionally calls onEnterView() when it first enters.
 */
export default function useInViewTime({
  onExitView,
  onEnterView,
  threshold = 0.1, // fraction of element that must be visible
}) {
  const ref = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // The element is in the viewport
            startTimeRef.current = Date.now();
            onEnterView && onEnterView();
          } else {
            // The element is out of viewport
            if (startTimeRef.current) {
              const now = Date.now();
              const deltaSeconds = (now - startTimeRef.current) / 1000;
              startTimeRef.current = null;
              onExitView && onExitView(deltaSeconds);
            }
          }
        });
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    // Cleanup
    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [onExitView, onEnterView, threshold]);

  return ref;
}
